import React, {useEffect, useRef, useState} from 'react';
import {Button, Input, Tag} from 'uikit';
import CrossIcon from "uikit/src/assets/icons/16/cross.svg";
import ChevronIcon from "uikit/src/assets/icons/16/chevron.svg";
import styles from './PromocodeChips.module.scss';
import {
    PromocodeChipsInterface,
    PromocodeChipsProps
} from "@/views/Admin/Events/Event/Promocodes/Form/components/PromocodeChips.interface";
import PlusIcon from "uikit/src/assets/icons/16/plus.svg";
import {hasEmoji} from "@/views/Admin/Events/Event/Promocodes/Form/ValidateEmoji";


const PromocodeChips: React.FC<PromocodeChipsProps> = ({
                                                           value,
                                                           label,
                                                           placeholder,
                                                           error,
                                                           disabled = false,
                                                           onAdd,
                                                           onRemove,
                                                       }) => {
    const [input, setInput] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const chipsRef = useRef<HTMLDivElement>(null);
    const blurTimeout = useRef<NodeJS.Timeout | null>(null);

    const newChips = value.filter(item => item.id === null);
    const savedChips = value.filter(item => item.id !== null);

    useEffect(() => {
        const el = chipsRef.current;
        if (el && !showAll) {
            setIsOverflowing(el.scrollHeight > el.clientHeight);
        }
    }, [savedChips, showAll]);

    const handleAdd = () => {
        const rawCodes = input
            .split(/[\s,]+/)
            .map(str => str.trim())
            .filter(Boolean);

        const uniqueInputCodes = Array.from(new Set(rawCodes));
        const existingCodesSet = new Set(value.map(item => item.code));

        const newCodes: PromocodeChipsInterface[] = [];

        for (const code of uniqueInputCodes) {
            // пропускаем коды с эмодзи
            if (hasEmoji(code)) {
                continue;
            }
            if (existingCodesSet.has(code)) {
                newCodes.push({ id: null, code, error: 'Дубликат' });
            } else {
                newCodes.push({ id: null, code, error: '' });
            }
        }

        const filteredNew = newCodes.filter(item => !existingCodesSet.has(item.code));

        if (filteredNew.length > 0) {
            onAdd(filteredNew);
        }

        setInput('');
    };



    return (
        <div className={styles.chipsRow} data-testid="promocodes-wrapper">
            <div className={styles.inputWrapper}>
                <Input
                    data-testid="promocodes-input"
                    value={input}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    disabled={disabled}
                    onChange={({target}) => setInput(target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (blurTimeout.current) clearTimeout(blurTimeout.current);
                            handleAdd();
                        }
                    }}
                    onBlur={() => {
                        blurTimeout.current = setTimeout(() => {
                            handleAdd();
                        }, 150);
                    }}
                    size="small"
                />
                <Button type={'secondary'}
                        theme={'default'}
                        size={'small'}
                        onClick={(e) => {
                            e.preventDefault();
                            if (blurTimeout.current) clearTimeout(blurTimeout.current);
                            handleAdd();
                        }}
                >
                    <PlusIcon/>
                </Button>
            </div>
            {newChips.length > 0 && <div
                className={`${styles.chipsList} ${newChips.length ? styles.newChipsList : ''}`}
                data-testid="promocodes-list-new"
            >
                {newChips.map(({code, error}, index) => (
                    <Tag
                        key={`new-${index}`}
                        type={!error ? 'events' : 'danger'}
                        size="medium"
                        title={error || ''}
                        data-testid={`promocode-tag-${index}`}
                    >
                        {code}
                        <CrossIcon
                            className={styles.chips}
                            data-testid={`remove-tag-${index}`}
                            onClick={() => onRemove(code)}
                        />
                    </Tag>
                ))}

            </div>}

            <div
                ref={chipsRef}
                className={`${styles.chipsList} ${showAll ? styles.chipsListExpanded : ''}`}
                data-testid="promocodes-list"
            >
                {savedChips.map(({code, error}, index) => (
                    <Tag
                        key={`saved-${index}`}
                        type={!error ? 'secondary' : 'danger'}
                        size="small"
                        title={error || ''}
                        data-testid={`promocode-tag-saved-${index}`}
                    >
                        {code}
                    </Tag>
                ))}
            </div>

            {(isOverflowing || showAll) && (
                <div
                    className={styles.showAll}
                    onClick={() => setShowAll(prev => !prev)}
                    data-testid="promocodes-toggle"
                >
                    {showAll ? 'Скрыть' : 'Показать все'}
                    <ChevronIcon
                        className={showAll ? styles.chevronUp : styles.chevronDown}
                    />
                </div>
            )}
        </div>
    );
};

export default PromocodeChips;
